import React, { useEffect } from "react";

import { Plural, Trans } from "@lingui/react/macro";
import { isEmpty } from "lodash/fp";

import RadioGroup from "@mui/material/RadioGroup";

import {
  EmptyStateContainer,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@readai/common/components/EmptyStateContainer";
import Stack from "@readai/pulse/components/Stack";

import useSchedulingStore from "@app/booking/stores/scheduling";
import type { PublicRssLink } from "@app/booking/types/rssLinkTypes";

import { Header, Heading, Radio, RadioControlLabel } from "./styles";

const EmptyState = () => (
  <EmptyStateContainer border="none">
    <EmptyStateTitle>
      <Trans>No durations available</Trans>
    </EmptyStateTitle>
    <EmptyStateSubtext>
      <Trans>
        Please reach out to the calendar owner for a new scheduling link or more
        information.
      </Trans>
    </EmptyStateSubtext>
  </EmptyStateContainer>
);

const ChooseDuration = ({
  links,
  onContinue,
}: {
  links: PublicRssLink[];
  onContinue?: (linkVanityId?: string) => void;
}) => {
  const { constraints, setConstraints, resetSelections } = useSchedulingStore(
    (state) => ({
      constraints: state.constraints,
      setConstraints: state.setConstraints,
      resetSelections: state.resetSelections,
    }),
  );

  useEffect(() => {
    resetSelections();
  }, []);

  const handleSelectDuration = (link: PublicRssLink) => () => {
    setConstraints(link);
    onContinue?.(link.vanityId);
  };

  return (
    <Stack
      variant="center"
      spacing={3}
      sx={{ textAlign: "center", px: { xs: 2, lg: 5 }, pb: 3 }}
    >
      <Header>
        <Heading>
          <Trans>Select your preferred meeting duration</Trans>
        </Heading>
      </Header>
      <RadioGroup>
        <Stack variant="center" spacing={1}>
          {isEmpty(links) ? (
            <EmptyState />
          ) : (
            links.map((link) => (
              <RadioControlLabel
                checked={link.allowedDuration === constraints?.allowedDuration}
                key={link.vanityId}
                control={
                  <Radio size="small" onClick={handleSelectDuration(link)} />
                }
                label={
                  <Plural
                    value={link.allowedDuration}
                    one="# minute"
                    other="# minutes"
                  />
                }
              />
            ))
          )}
        </Stack>
      </RadioGroup>
    </Stack>
  );
};

export default ChooseDuration;
